import React, { useEffect, useState } from 'react';
import { Box, Button, CopyButton, Group, Modal, Text, TextInput, Anchor, Select, Loader, Popover, ActionIcon, Switch } from '@mantine/core';
import {
  IconEye,
  IconBolt,
  IconCheck,
  IconCopy,
  IconFilter,
  IconArrowUpRight,
  IconPlayerPlayFilled,
  IconCircle,
  IconChevronDown,
  IconChevronUp,
  IconAlertTriangle,
  IconKey,
  IconCircleCheck,
  IconRadar2,
  IconClockHour4,
  IconRefresh,
  IconHelp,
  IconArrowBackUp,
  IconChevronLeft,
} from '@tabler/icons-react';
import { WebhookOption, totalEstRange, setupStepsFor, clickfunnelsSetupSteps, credentialFieldsFor, credentialsRequiredFor, AUTO_CREATE_PROCESSORS, CONVERSION_TYPE_PROCESSORS, AUTO_CREATE_HELPER_TEXT, REQUIRED_CREDENTIAL_HELPER_TEXT, INTEGRATION_LOGIN, webhookProcessor, webhookSlug } from './types';
import { webhookTestStep, STRIPE_CHECKOUT_DOC_URL } from '../../components/GeneralComponent/webhookTestSteps';
import { platformGuidesFor, ADVANCED_WEBHOOK_DOC_URL } from '../../components/GeneralComponent/platformSpecificGuides';
import { useOnboardingStyles } from './onboardingStyles';
import { useOnbColors } from './onboardingColors';
import { StepVideo, SuccessCheck, StepImage, notify } from './common';
import SettingsApi from '../../api/SettingsApi';
import FunnelApi from '../../api/FunnelApi';

type Phase = 'confirm' | 'configure' | 'generated' | 'steps' | 'complete';

export interface GuidedIntegration extends WebhookOption {
  name: string;
}

interface Props {
  opened: boolean;
  integrations: GuidedIntegration[];
  funnelName: string;
  systemWebsiteId: string;
  funnelId: string;
  onFunnelRenamed: (name: string) => void;
  onClose: () => void;
  onComplete: () => void;
  onSkip: () => void;
}

// Rounded brand tile — logo image, or a purple monogram for custom
function Mark({ item, size = 64 }: { item: WebhookOption; size?: number }) {
  const { classes } = useOnboardingStyles();
  const col = useOnbColors();
  if (item.logo) {
    return (
      <div style={{ width: size, height: size, borderRadius: 16, background: col.card, border: `1px solid ${col.border}`, display: 'grid', placeItems: 'center' }}>
        <img src={item.logo} alt={item.label} style={{ width: size * 0.62, height: size * 0.62, objectFit: 'contain' }} />
      </div>
    );
  }
  return <div className={classes.customTile} style={{ width: size, height: size, borderRadius: 16, fontWeight: 800 }}>⚡</div>;
}

export function GuidedSetup({ opened, integrations, funnelName, systemWebsiteId, funnelId, onFunnelRenamed, onClose, onComplete, onSkip }: Props) {
  const { classes, cx } = useOnboardingStyles();
  const col = useOnbColors();
  const [phase, setPhase] = useState<Phase>('confirm');
  const [intIdx, setIntIdx] = useState(0);
  const [subStep, setSubStep] = useState(0);
  const [names, setNames] = useState<Record<string, string>>({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [webhookId, setWebhookId] = useState('');
  const [verified, setVerified] = useState(false);
  const [verifyingNow, setVerifyingNow] = useState(false);
  const [noEventYet, setNoEventYet] = useState(false);
  const [autoCreateWarning, setAutoCreateWarning] = useState('');
  // True when VisiOpt tried to create the endpoint in the provider on this save (creds were supplied).
  const [autoCreateAttempted, setAutoCreateAttempted] = useState(false);
  const [funnelModalOpen, setFunnelModalOpen] = useState(false);
  const [funnelNameInput, setFunnelNameInput] = useState('');
  const [funnelError, setFunnelError] = useState('');
  const [savingFunnel, setSavingFunnel] = useState(false);
  // Per-integration credential values (login_id, secret_key, etc.)
  const [creds, setCreds] = useState<Record<string, Record<string, string>>>({});
  const [cfConversionType, setCfConversionType] = useState('sales');
  const [convType, setConvType] = useState('sale');
  /* Drives Customer Type: conversions on this webhook count as purchases. Same field name,
     same default and same 1/0 payload the Settings webhook screen sends - see
     components/Settings/PaymentProcessingAndCarts. */
  const [countAsPurchase, setCountAsPurchase] = useState(false);

  // Reset the flow each time it opens — starts on the confirmation dialog
  useEffect(() => {
    if (opened) {
      setPhase('confirm');
      setIntIdx(0);
      setSubStep(0);
      setGenerating(false);
      setGenError('');
      setGeneratedUrl('');
      setAutoCreateWarning('');
      setAutoCreateAttempted(false);
      setWebhookId('');
      setVerified(false);
      setVerifyingNow(false);
      setNoEventYet(false);
      setFunnelModalOpen(false);
      setCfConversionType('sales');
      setConvType('sale');
      setCountAsPurchase(false);
      setNames(Object.fromEntries(integrations.map((i) => [i.value, i.name || `${i.label} Events`])));
      // Seed credential values — select fields default to their first option
      setCreds(
        Object.fromEntries(
          integrations.map((i) => {
            const fields = credentialFieldsFor(webhookProcessor(i));
            const seed: Record<string, string> = {};
            fields.forEach((f) => {
              seed[f.key] = f.type === 'select' && f.options ? f.options[0].value : '';
            });
            return [i.value, seed];
          })
        )
      );
    }
  }, [opened]); // eslint-disable-line

  // Read the verification status once when a webhook becomes available. There is no
  // interval — the user refreshes on demand with the Check / Verify Connection button.
  useEffect(() => {
    if (!opened || !webhookId || verified) return;
    let active = true;
    SettingsApi.getwebhookverificationstatus({ webhook_id: webhookId })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (active && res && res.status === 1 && res.data && Number(res.data.verification_status) === 1) {
          setVerified(true);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [opened, webhookId]);

  if (!integrations.length) return null;

  const n = integrations.length;
  const current = integrations[Math.min(intIdx, n - 1)];
  const nextItem = integrations[intIdx + 1];
  const webhookName = names[current.value] ?? `${current.label} Events`;
  const url = generatedUrl;
  const est = totalEstRange(integrations.map((i) => i.value));
  // ClickFunnels manual steps depend on the chosen conversion type (Classic/2.0 × Sales/Opt-in)
  const steps =
    webhookProcessor(current) === 'clickfunnels' ? clickfunnelsSetupSteps(cfConversionType) : setupStepsFor(current.value);
  const activeStep = steps[Math.min(subStep, steps.length - 1)];

  // A processor "supports test webhooks" exactly when we have instructions for firing one.
  const testStep = webhookTestStep(webhookProcessor(current));
  const supportsTest = Boolean(testStep);
  const platformGuides = platformGuidesFor(webhookProcessor(current));
  // Stripe gets an extra hosted-checkout note before Generate
  const isStripe = webhookProcessor(current) === 'stripe';
  // Derived from the save response: attempted with no warning = created; attempted with warning = failed
  const autoCreated = autoCreateAttempted && !autoCreateWarning;

  // One-shot status check behind the "Verify Connection" button (the 5s poll still runs).
  const verifyNow = () => {
    if (!webhookId) return;
    setVerifyingNow(true);
    setNoEventYet(false);
    SettingsApi.getwebhookverificationstatus({ webhook_id: webhookId })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        setVerifyingNow(false);
        if (res && res.status === 1 && res.data && Number(res.data.verification_status) === 1) {
          setVerified(true);
        } else {
          setNoEventYet(true);
        }
      })
      .catch(() => {
        setVerifyingNow(false);
        setNoEventYet(true);
      });
  };

  // Verification status, shown only once the integration's setup is complete.
  // Test-capable processors also get a recommendation + instructions + Verify Connection.
  const verificationBlock = (
    <Box style={{ width: '100%', marginTop: 14, textAlign: 'left' }}>
      {verified ? (
        <Box style={{ display: 'flex', alignItems: 'center', gap: 8, background: col.greenBg, border: `1px solid ${col.greenBorder}`, borderRadius: 10, padding: '12px 14px' }}>
          <IconCircleCheck size={18} color={col.green} />
          <Text fz={13.5} fw={600} c={col.greenDeep}>
            Verified — Visiopt received an event from {current.label}.
          </Text>
        </Box>
      ) : (
        <Box style={{ background: col.amberBg, border: `1px solid ${col.amberBorder}`, borderRadius: 12, padding: '12px 14px' }}>
          <Group spacing={8} noWrap>
            {/* Static icon: we no longer poll, so a spinner would imply live checking */}
            {verifyingNow ? <Loader size="xs" color="orange" /> : <IconClockHour4 size={18} color={col.amber} />}
            <Text fz={13.5} fw={700} c={col.amberText}>
              {verifyingNow ? 'Checking…' : 'Waiting for First Event'}
            </Text>
          </Group>

          {supportsTest ? (
            <>
              <Text fz={13} c={col.amberText} mt={4}>
                <b>Recommended:</b> send a test webhook from {current.label} to verify your integration now. Verification also
                happens automatically after your first real {current.label} event.
              </Text>
              <Text fz={13} fw={600} c={col.amberText} mt={10}>
                {testStep!.text}
              </Text>
              {testStep!.image && (
                <StepImage
                  key={testStep!.image}
                  src={testStep!.image}
                  alt={`Send a test webhook from ${current.label}`}
                  borderColor={col.amberBorder}
                  radius={10}
                />
              )}
              <Group spacing={10} mt={12} noWrap>
                <Button
                  size="sm"
                  radius="md"
                  color="dark"
                  loading={verifyingNow}
                  leftIcon={!verifyingNow ? <IconRadar2 size={15} /> : undefined}
                  onClick={verifyNow}
                >
                  Verify Connection
                </Button>
                {noEventYet && (
                  <Text fz={12.5} c={col.amberText}>
                    No event received yet. Send the test webhook, then try again.
                  </Text>
                )}
              </Group>
            </>
          ) : (
            <>
              <Text fz={13} c={col.amberText} mt={4}>
                {current.label} doesn't support sending a test event. Visiopt will verify this webhook automatically as soon as
                your first real {current.label} event is received — there's nothing else to do.
              </Text>
              <Group spacing={10} mt={12} noWrap>
                <Button
                  size="sm"
                  radius="md"
                  variant="default"
                  loading={verifyingNow}
                  leftIcon={!verifyingNow ? <IconRefresh size={15} /> : undefined}
                  onClick={verifyNow}
                >
                  Check Again
                </Button>
                {noEventYet && (
                  <Text fz={12.5} c={col.amberText}>
                    No event received yet.
                  </Text>
                )}
              </Group>
            </>
          )}
        </Box>
      )}
    </Box>
  );

  // Reuse a webhook this user already created for the same processor + name, so
  // re-entering the wizard does not fail on the backend's duplicate-name check.
  const findExistingWebhook = (name: string) =>
    SettingsApi.getwebhooklist({ system_website_id: systemWebsiteId, data_type: webhookProcessor(current) })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        if (!res || res.status !== 1 || !res.data || !res.data.webhook_table_data) return null;
        const rows: any[] = Object.values(res.data.webhook_table_data);
        return rows.find((w) => (w.webhook_name || '').trim() === name) || null;
      })
      .catch(() => null);

  // Save the webhook via the existing API, then surface the returned URL
  const generateWebhook = async () => {
    setGenError('');
    const name = webhookName.trim();
    if (!name) {
      setGenError('Please enter a webhook name.');
      return;
    }
    // Don't let two selected integrations use the same webhook name
    const nameLower = name.toLowerCase();
    const clash = integrations.some(
      (i) => i.value !== current.value && (names[i.value] ?? `${i.label} Events`).trim().toLowerCase() === nameLower
    );
    if (clash) {
      setGenError('You already used this webhook name for another integration. Please choose a different name.');
      return;
    }
    if (!funnelId) {
      setGenError('Please create a funnel before generating a webhook.');
      return;
    }
    const credValues = creds[current.value] || {};
    const credFields = credentialFieldsFor(webhookProcessor(current));
    // Credentials are mandatory only for required-credential providers (Stripe, authorize.net).
    // For every other provider they are optional — blank just means "add the webhook manually".
    if (credentialsRequiredFor(webhookProcessor(current))) {
      const missing = credFields.find((f) => f.type === 'text' && !(credValues[f.key] || '').trim());
      if (missing) {
        setGenError(`${missing.label} is required for ${current.label}.`);
        return;
      }
    }
    setGenerating(true);
    try {
      const res = await SettingsApi.insertwebhooks({
        system_website_id: systemWebsiteId,
        webhook_name: JSON.stringify(name),
        webhook_processor: webhookProcessor(current),
        webhook_slug: webhookSlug(current),
        funnel_id: [funnelId],
        account_type: 'SANDBOX',
        ...credValues,
        ...(webhookProcessor(current) === 'clickfunnels' ? { clickfunnels_conversion_type: cfConversionType } : {}),
        ...(CONVERSION_TYPE_PROCESSORS.includes(webhookProcessor(current)) ? { conversion_type: convType } : {}),
        /* Sent for every integration, not only one: settings_api.php reads it the same way
           for all processors, and defaults it to 0 when the field is absent. */
        count_as_purchase: countAsPurchase ? 1 : 0,
      }).then((r: Response) => (r.ok ? r.json() : null));
      if (res && res.status === 1 && res.data && res.data.webhook_url) {
        setGenerating(false);
        setGeneratedUrl(res.data.webhook_url);
        setWebhookId(res.data.webhook_id || '');
        setVerified(false);
        // The provider creates the endpoint when credentials were supplied; report which way it went
        setAutoCreateWarning(res.data.stripe_warning || '');
        setAutoCreateAttempted(Number(res.data.auto_create_attempted) === 1);
        setPhase('generated');
        return;
      }
      // Same name already exists: reuse it instead of dead-ending the user on re-entry
      if (res && res.error_code === 'webhook_name_exists') {
        const existing = await findExistingWebhook(name);
        setGenerating(false);
        if (existing && existing.webhook_url) {
          setGeneratedUrl(existing.webhook_url);
          setWebhookId(existing.webhook_id || '');
          setVerified(Number(existing.verification_status) === 1);
          // Reused an existing webhook — we did not create anything in Stripe on this request
          setAutoCreateWarning('');
          setAutoCreateAttempted(false);
          setPhase('generated');
        } else {
          setGenError('That webhook name is already in use. Please enter a different name.');
        }
        return;
      }
      setGenerating(false);
      setGenError(res && res.message ? res.message : 'Could not generate the webhook URL. Please try again.');
    } catch {
      setGenerating(false);
      setGenError('Could not generate the webhook URL. Please try again.');
    }
  };

  // Rename the funnel created in the funnel step via the existing add_funnel API
  const openFunnelModal = () => {
    setFunnelNameInput(funnelName || '');
    setFunnelError('');
    setFunnelModalOpen(true);
  };

  const saveFunnelName = () => {
    const name = funnelNameInput.trim();
    setFunnelError('');
    if (!name) {
      setFunnelError('Funnel name is required.');
      return;
    }
    setSavingFunnel(true);
    FunnelApi.addfunnel({
      system_website_id: systemWebsiteId,
      name,
      funnel_id: funnelId,
      account_funnel_limit: 1000,
    })
      .then((r: Response) => (r.ok ? r.json() : null))
      .then((res: any) => {
        setSavingFunnel(false);
        if (res && res.status === 1) {
          onFunnelRenamed(name);
          setFunnelModalOpen(false);
          notify('success', 'Funnel name updated.');
        } else {
          setFunnelError(res && res.message ? res.message : 'Could not update the funnel name.');
        }
      })
      .catch(() => {
        setSavingFunnel(false);
        setFunnelError('Could not update the funnel name.');
      });
  };

  // Custom integrations have no platform to sign in to, so we go straight to the steps.
  const loginUrl = INTEGRATION_LOGIN[current.value] || '';

  const openIntegrationSite = () => {
    if (loginUrl) window.open(loginUrl, '_blank', 'noopener,noreferrer');
    setSubStep(0);
    setPhase('steps');
  };

  // Jump to a specific integration (forward or back), resetting its per-integration state
  const gotoIntegration = (idx: number) => {
    if (idx < 0 || idx > n - 1 || idx === intIdx) return;
    setIntIdx(idx);
    setSubStep(0);
    setGeneratedUrl('');
    setAutoCreateWarning('');
    setAutoCreateAttempted(false);
    setWebhookId('');
    setVerified(false);
    setNoEventYet(false);
    setGenError('');
    setCfConversionType('sales');
    setConvType('sale');
    setCountAsPurchase(false);
    setPhase('configure');
  };

  const gotoNextIntegration = () => {
    if (intIdx < n - 1) gotoIntegration(intIdx + 1);
    else onComplete();
  };

  const gotoPrevIntegration = () => gotoIntegration(intIdx - 1);

  const Pills = () => (
    <>
      <Text ta="center" fw={700} fz="sm" c={col.purple} mt={4}>
        Integration {intIdx + 1} of {n}: {current.label}
      </Text>
      <div className={classes.gsPills}>
        {integrations.map((it, i) => {
          const done = i < intIdx;
          const active = i === intIdx;
          return (
            <span
              key={it.value}
              className={cx(classes.gsPill, { [classes.gsPillActive]: active, [classes.gsPillDone]: done })}
              // Completed integrations can be revisited
              style={done ? { cursor: 'pointer' } : undefined}
              onClick={done ? () => gotoIntegration(i) : undefined}
              title={done ? `Go back to ${it.label}` : undefined}
            >
              {done ? <IconCheck size={13} /> : active ? <IconPlayerPlayFilled size={11} /> : <IconCircle size={11} />}
              {it.label}
            </span>
          );
        })}
      </div>
    </>
  );

  const UrlBanner = () => (
    <div className={classes.gsUrlBanner}>
      <div>
        <Text fz={11} fw={700} c={col.purple} style={{ letterSpacing: '0.04em' }}>
          WEBHOOK URL
        </Text>
        <span className={classes.gsUrlText}>{url}</span>
      </div>
      <CopyButton value={url} timeout={1500}>
        {({ copied, copy }) => (
          <Button size="xs" radius="md" color="dark" leftIcon={<IconCopy size={14} />} onClick={copy}>
            {copied ? 'Copied' : 'Copy Again'}
          </Button>
        )}
      </CopyButton>
    </div>
  );

  const centered: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 6 };

  return (
    <>
      {/* Confirmation dialog */}
      <Modal opened={opened && phase === 'confirm'} onClose={onClose} withCloseButton={false} centered radius="lg" size={460}>
        <Box style={{ ...centered, gap: 10, padding: 6 }}>
          <div className={classes.customTile} style={{ width: 52, height: 52, borderRadius: 14 }}>
            <IconBolt size={24} />
          </div>
          <Text fz={22} c={col.textTitle} style={{ fontFamily: 'Georgia, serif' }}>
            Ready to Start Guided Setup?
          </Text>
          <Text c="dimmed" fz="sm">
            We'll guide you through connecting each selected integration, one step at a time.
          </Text>
          <Box style={{ width: 'auto', display: 'grid', gap: 8, margin: '6px auto', justifyItems: 'start' }}>
            {integrations.map((it) => (
              <Group key={it.value} spacing={9} noWrap>
                <IconCheck size={17} color={col.green} />
                <Text fw={700} fz="sm" c={col.textPrimary}>
                  {it.label}
                </Text>
              </Group>
            ))}
          </Box>
          <div className={classes.estPill} style={{ width: '100%' }}>
            Estimated setup time: Approximately {est.low}–{est.high} minutes for {n} integration{n > 1 ? 's' : ''}.
          </div>
          <Group mt={6}>
            <Button variant="default" radius="md" onClick={onClose}>
              Back
            </Button>
            <Button className={classes.gsPurpleBtn} h={44} onClick={() => setPhase('configure')}>
              Start Guided Setup
            </Button>
          </Group>
          <Button variant="subtle" color="gray" size="sm" mt={2} onClick={onSkip}>
            Skip for now
          </Button>
        </Box>
      </Modal>

      {/* Wizard */}
      <Modal
        opened={opened && phase !== 'confirm'}
        onClose={onClose}
        centered
        radius="lg"
        size={720}
        title={
          <Group spacing={8}>
            <IconEye size={20} color={col.purple} />
            <Text fw={700}>Connect Your Integrations</Text>
          </Group>
        }
        styles={{ header: { borderBottom: `1px solid ${col.border}`, marginBottom: 14 } }}
      >
        <Text ta="center" c="dimmed" fz="sm">
          We'll guide you through each selected integration one at a time.
        </Text>
        <Pills />
        <Group position="center" spacing={14} mt={4}>
          {/* Go back to the previous integration */}
          {intIdx > 0 && (
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              leftIcon={<IconChevronLeft size={14} />}
              onClick={gotoPrevIntegration}
            >
              Back to {integrations[intIdx - 1].label}
            </Button>
          )}
          {/* Skip just this integration and move to the next one (finishes if it's the last) */}
          <Button variant="subtle" color="gray" size="xs" onClick={gotoNextIntegration}>
            {nextItem ? `Skip ${current.label} for now` : `Skip ${current.label}`}
          </Button>
        </Group>

        {/* Configure */}
        {phase === 'configure' && (
          <Box style={{ ...centered, marginTop: 18 }}>
            <Mark item={current} />
            <Text fz={26} style={{ fontFamily: 'Georgia, serif' }}>
              Configure {current.label} Webhook
            </Text>
            <Text c="dimmed" fz="sm" maw={460}>
              We'll generate a secure webhook URL that {current.label} will use to send important events
              directly to Visiopt.
            </Text>
            <StepVideo modalTitle={`Connecting ${current.label}`} sublabel="A 45-second walkthrough of this step" />
            <Box style={{ width: '100%', textAlign: 'left', marginTop: 8 }}>
              <Text className={classes.fieldLabel}>Webhook Name</Text>
              <TextInput
                size="md"
                radius="md"
                value={webhookName}
                onChange={(e) => {
                  const val = e.currentTarget.value;
                  setNames((p) => ({ ...p, [current.value]: val }));
                }}
              />
              <Text className={classes.fieldHelp}>Give your webhook a descriptive name so it's easy to recognize later.</Text>
              <div className={classes.gsUrlBanner} style={{ marginTop: 6 }}>
                <Group spacing={10} noWrap>
                  <div className={classes.detectIcon}>
                    <IconFilter size={18} />
                  </div>
                  <div>
                    <Text fz={12} c="dimmed">
                      Tracking Funnel
                    </Text>
                    <Text fw={700} fz="sm" c={col.textPrimary}>
                      {funnelName || 'Main Sales Funnel'}
                    </Text>
                  </div>
                </Group>
                <Anchor fz="sm" fw={700} c={col.purple} onClick={openFunnelModal}>
                  Rename Funnel
                </Anchor>
              </div>

              {/* ClickFunnels: the credentials below only apply to 2.0 auto-creation */}
              {webhookProcessor(current) === 'clickfunnels' && (
                <div className={classes.qaBox} style={{ background: col.purpleBg2, borderColor: col.purpleBorder, marginTop: 14 }}>
                  <Group spacing={7} noWrap align="flex-start">
                    <IconBolt size={16} color={col.purple} style={{ flexShrink: 0, marginTop: 2 }} />
                    <Text className={classes.qaText} c={col.purple} fw={600}>
                      The credentials below are only needed if you're on ClickFunnels 2.0 and want Visiopt to create the webhook automatically. On ClickFunnels Classic, skip them and follow the manual setup steps.
                    </Text>
                  </Group>
                </div>
              )}

              {/* Processor credentials + step-by-step help — sourced from the shared config (same as Settings) */}
              {(() => {
                const procKey = webhookProcessor(current);
                // Shopify auto-creates via its OAuth connection (Step 2), not manual credentials — same as Settings
                if (procKey === 'shopify') return null;
                const credFieldsRender = credentialFieldsFor(procKey);
                if (!credFieldsRender.length) return null;
                const required = credentialsRequiredFor(procKey);
                const optional = AUTO_CREATE_PROCESSORS.includes(procKey);
                return (
                  <>
                    {(required || optional) && (
                      <div className={classes.qaBox} style={{ background: required ? col.redBg : col.purpleBg2, borderColor: required ? col.redBorder : col.purpleBorder, marginTop: 14 }}>
                        <Group spacing={7} noWrap align="flex-start">
                          <IconKey size={16} color={required ? col.red : col.purple} style={{ flexShrink: 0, marginTop: 2 }} />
                          <Text className={classes.qaText} c={required ? col.red : col.purple} fw={600}>
                            {required ? REQUIRED_CREDENTIAL_HELPER_TEXT : AUTO_CREATE_HELPER_TEXT}
                          </Text>
                        </Group>
                      </div>
                    )}
                    {credFieldsRender.map((field) => (
                      <Box key={field.key} mt={12}>
                        <Text className={classes.fieldLabel}>
                          {field.label}
                          {required ? (
                            <span style={{ color: col.red }}> *</span>
                          ) : optional ? (
                            <span style={{ color: col.textMuted, fontWeight: 400, fontSize: 12 }}> (Optional)</span>
                          ) : null}
                        </Text>
                        {field.type === 'select' && field.options ? (
                          <Select
                            size="md"
                            radius="md"
                            data={field.options}
                            value={(creds[current.value] && creds[current.value][field.key]) || ''}
                            onChange={(val) =>
                              setCreds((p) => ({ ...p, [current.value]: { ...p[current.value], [field.key]: val || '' } }))
                            }
                          />
                        ) : (
                          <TextInput
                            size="md"
                            radius="md"
                            placeholder={field.placeholder || `Enter your ${current.label} ${field.label}`}
                            value={(creds[current.value] && creds[current.value][field.key]) || ''}
                            onChange={(e) => {
                              const val = e.currentTarget.value;
                              setCreds((p) => ({ ...p, [current.value]: { ...p[current.value], [field.key]: val } }));
                              if (genError) setGenError('');
                            }}
                          />
                        )}
                        {field.help && (
                          <div className={classes.qaBox} style={{ background: col.surface, borderColor: col.border, marginTop: 8 }}>
                            <Group spacing={7} mb={5} noWrap>
                              <IconKey size={14} color={col.purple} />
                              <Text fw={700} fz={13} c={col.textPrimary}>
                                {field.help.title}
                              </Text>
                            </Group>
                            <Box style={{ display: 'grid', gap: 4 }}>
                              {field.help.steps.map((s, i) => (
                                <Text key={i} className={classes.qaText}>
                                  {s}
                                </Text>
                              ))}
                            </Box>
                            {field.help.link && (
                              <Anchor href={field.help.link} target="_blank" rel="noreferrer" fz="sm" fw={700} c={col.purple} mt={8} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <IconArrowUpRight size={15} /> {field.help.linkLabel || 'Open documentation'}
                              </Anchor>
                            )}
                          </div>
                        )}
                      </Box>
                    ))}
                  </>
                );
              })()}

              {/* Sale vs Opt-in for providers that fire both event kinds */}
              {CONVERSION_TYPE_PROCESSORS.includes(webhookProcessor(current)) && (
                <Box mt={14}>
                  <Text className={classes.fieldLabel}>Conversion Type</Text>
                  <Select
                    size="md"
                    radius="md"
                    data={[
                      { value: 'sale', label: 'Sale (Purchase)' },
                      { value: 'optin', label: 'Opt-in (Lead)' },
                    ]}
                    value={convType}
                    onChange={(val) => setConvType(val || 'sale')}
                  />
                  <Text className={classes.fieldHelp}>
                    Choose <b>Sale</b> to track purchases, or <b>Opt-in</b> to track leads/form submissions for this webhook.
                  </Text>
                </Box>
              )}

              {/* Count as a purchase - offered for every integration, because any of them can
                  carry the purchase event that Customer Type classifies. */}
              <Box mt={14}>
                <Group spacing="xs" noWrap align="center">
                  <Text className={classes.fieldLabel} style={{ marginBottom: 0 }}>Count as Purchase</Text>
                  <Switch
                    size="md"
                    onLabel="ON"
                    offLabel="OFF"
                    checked={countAsPurchase}
                    onChange={(event) => setCountAsPurchase(event.currentTarget.checked)}
                  />
                </Group>
                <Text className={classes.fieldHelp}>
                  Turn on only if this webhook records a sale. Leave off for leads, sign-ups or booked calls.
                  If two webhooks report the same order, turn it on for only one.
                </Text>
              </Box>

              {/* Calendly has no webhook UI — without the token, manual setup means calling their API */}
              {webhookProcessor(current) === 'calendly' && (
                <div className={classes.qaBox} style={{ background: col.amberBg, borderColor: col.amberBorder, marginTop: 12 }}>
                  <Group spacing={7} noWrap align="flex-start">
                    <IconAlertTriangle size={16} color={col.amber} style={{ flexShrink: 0, marginTop: 2 }} />
                    <Text className={classes.qaText} c={col.amberText}>
                      Calendly has no webhook screen in its dashboard. Enter the Access Token above and Visiopt will
                      create the webhook for you automatically. If you leave it blank, the webhook has to be created
                      through Calendly's API, which requires a developer.
                    </Text>
                  </Group>
                </div>
              )}

              {/* ClickFunnels: pick which event the auto-created webhook fires on */}
              {webhookProcessor(current) === 'clickfunnels' && (
                <Box mt={12}>
                  <Group spacing={6} noWrap align="center" mb={2}>
                    <Text className={classes.fieldLabel} style={{ marginBottom: 0 }}>
                      Conversion Type
                    </Text>
                    <Popover width={320} position="right" withArrow shadow="md">
                      <Popover.Target>
                        <ActionIcon variant="light" color="grape" size="sm" radius="xl" aria-label="About conversion type">
                          <IconHelp size={15} />
                        </ActionIcon>
                      </Popover.Target>
                      <Popover.Dropdown>
                        <Text fw={700} fz={13} mb={6}>
                          Which conversion should this webhook track?
                        </Text>
                        <Text fz={12.5} c="dimmed" mb={4}>
                          <b>Sales</b> — fires when a purchase/order is completed.
                        </Text>
                        <Text fz={12.5} c="dimmed" mb={8}>
                          <b>Opt-in</b> — fires when a contact/lead is captured (no purchase).
                        </Text>
                        <Text fz={12.5} c="dimmed" mb={8}>
                          Pick <b>ClickFunnels Classic</b> or <b>2.0</b> to match the version your funnel is built on.
                        </Text>
                        <Text fz={12.5} c="dimmed">
                          This also decides which manual setup instructions we show if you don't enter API credentials
                          for automatic setup.
                        </Text>
                      </Popover.Dropdown>
                    </Popover>
                  </Group>
                  <Select
                    size="md"
                    radius="md"
                    data={[
                      { value: 'sales', label: 'Sales (Purchase Created) (ClickFunnels Classic)' },
                      { value: 'optin', label: 'Opt-in (Contact Created) (ClickFunnels Classic)' },
                      { value: 'sales_2', label: 'Sales (Order Created) (ClickFunnels 2.0)' },
                      { value: 'optin_2', label: 'Opt-in (Contact Created) (ClickFunnels 2.0)' },
                    ]}
                    value={cfConversionType}
                    onChange={(val) => setCfConversionType(val || 'sales')}
                  />
                  <Text className={classes.fieldHelp}>
                    Choose Classic or 2.0 to match your funnel, and whether to track purchases or opt-ins.
                  </Text>
                </Box>
              )}
              <Anchor
                fz="sm"
                fw={600}
                c={col.purple}
                mt={12}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                onClick={() => setAdvancedOpen((v) => !v)}
              >
                Advanced Options {advancedOpen ? <IconChevronUp size={15} /> : <IconChevronDown size={15} />}
              </Anchor>
              {advancedOpen && (
                <div className={classes.qaBox} style={{ background: col.purpleBg2, borderColor: col.purpleBorder, marginTop: 8 }}>
                  <Text className={classes.qaText}>
                    Advanced webhook settings — event filtering, custom payload mapping, and signing
                    secrets — are available in your Integrations settings after onboarding. Most setups
                    don't need these.
                  </Text>
                  <Anchor href="https://visiopt.com/help-videos/webhook-advanced.html" fz="sm" fw={700} c={col.purple} mt={10} style={{ display: 'inline-block' }} target="_blank">
                    View advanced documentation
                  </Anchor>
                </div>
              )}
            </Box>
            {/* Stripe-only: the hosted-checkout caveat. Other providers already show the optional-credentials note above the fields. */}
            {isStripe && (
              <Box style={{ width: '100%', textAlign: 'left', marginTop: 14, background: col.blueBg, border: `1px solid ${col.blueBorder}`, borderRadius: 10, padding: '12px 14px' }}>
                <Group spacing={8} noWrap align="flex-start">
                  <IconAlertTriangle size={18} color={col.blue} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <Text fz={13.5} fw={700} c={col.blueDeep}>
                      Using Stripe Payment Links, Hosted Checkout, or Embedded Checkout?
                    </Text>
                    <Text fz={13} c={col.blueText} mt={4}>
                      One extra step is required — complete the additional setup described here:{' '}
                      <Anchor href={STRIPE_CHECKOUT_DOC_URL} target="_blank" rel="noreferrer" fw={700} c={col.blue}>
                        View setup instructions
                      </Anchor>
                      . Otherwise, no additional setup is required.
                    </Text>
                  </div>
                </Group>
              </Box>
            )}
            {genError && (
              <Group spacing={6} mt={12} c={col.red} style={{ color: col.red }}>
                <IconAlertTriangle size={15} />
                <Text fz={13} c={col.red}>
                  {genError}
                </Text>
              </Group>
            )}
            <Button className={classes.gsPurpleBtn} mt={14} loading={generating} onClick={generateWebhook}>
              Generate Webhook URL
            </Button>
            <Text fz={12} c="dimmed" mt={4}>
              This only takes a few seconds.
            </Text>
          </Box>
        )}

        {/* Generated */}
        {phase === 'generated' && (
          <Box style={{ ...centered, marginTop: 18 }}>
            <SuccessCheck size={72} />
            <Text fw={800} fz={26} style={{ fontFamily: 'Georgia, serif' }}>
              Webhook Generated
            </Text>
            <Text c="dimmed" fz="sm" maw={460}>
              {autoCreated
                ? `Great! Visiopt created the webhook in your ${current.label} account — there is nothing to add manually.`
                : `Great! Your webhook has been generated. Next, we'll guide you through adding it to ${current.label}.`}
            </Text>

            {/* Auto-create result — shown only when credentials were supplied and creation was attempted */}
            {autoCreateAttempted && (
              <Box style={{ width: '100%', textAlign: 'left', marginTop: 12 }}>
                {autoCreateWarning ? (
                  <Box style={{ background: col.redBg, border: `1px solid ${col.redBorder}`, borderRadius: 10, padding: '12px 14px' }}>
                    <Group spacing={8} noWrap>
                      <IconAlertTriangle size={18} color={col.red} />
                      <Text fz={13.5} fw={700} c={col.redDeep}>
                        Automatic webhook creation failed
                      </Text>
                    </Group>
                    <Text fz={13} c={col.redDeep} mt={4}>
                      {autoCreateWarning}
                    </Text>
                    <Text fz={13} c={col.redDeep} mt={6}>
                      Your Visiopt configuration was still saved. Copy the webhook URL below and add the endpoint manually
                      in {current.label} — the next step walks you through it.
                    </Text>
                  </Box>
                ) : (
                  <Box style={{ background: col.greenBg, border: `1px solid ${col.greenBorder}`, borderRadius: 10, padding: '12px 14px' }}>
                    <Group spacing={8} noWrap>
                      <IconCircleCheck size={18} color={col.green} />
                      <Text fz={13.5} fw={700} c={col.greenDeep}>
                        Webhook created in {current.label}
                      </Text>
                    </Group>
                    <Text fz={13} c={col.greenText} mt={4}>
                      Visiopt added the endpoint and subscribed it to the required events in your {current.label} account —
                      no manual setup is required. You can skip the next screen.
                    </Text>
                    {isStripe && (
                      <Text fz={13} c={col.greenText} mt={6}>
                        Using <b>Stripe Payment Links</b>, <b>Stripe Hosted Checkout</b>, or <b>Embedded Checkout</b>? One
                        extra step is required:{' '}
                        <Anchor href={STRIPE_CHECKOUT_DOC_URL} target="_blank" rel="noreferrer" fw={700} c={col.greenText}>
                          View setup instructions
                        </Anchor>
                        .
                      </Text>
                    )}
                  </Box>
                )}
              </Box>
            )}
            <StepVideo duration="0:25" sublabel="A 25-second walkthrough of this step" modalTitle={`Adding the webhook to ${current.label}`} />
            <Box style={{ width: '100%', textAlign: 'left', marginTop: 8 }}>
              <Text className={classes.fieldLabel}>Webhook URL</Text>
              <Group spacing={10} noWrap>
                <TextInput style={{ flex: 1 }} size="md" radius="md" readOnly value={url} />
                <CopyButton value={url} timeout={1500}>
                  {({ copied, copy }) => (
                    <Button color="dark" radius="md" h={42} leftIcon={<IconCopy size={16} />} onClick={copy}>
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </Box>
            {autoCreated ? (
              <>
                {/* Auto-created. If the provider can fire a test event, go to the verification screen
                    so the user can confirm the connection; otherwise there's nothing to test — move on. */}
                {supportsTest ? (
                  <Button className={classes.gsPurpleBtn} mt={14} onClick={() => setPhase('complete')}>
                    Continue to Verification
                  </Button>
                ) : (
                  <Button className={classes.gsPurpleBtn} mt={14} onClick={gotoNextIntegration}>
                    {nextItem ? `Continue to ${nextItem.label}` : 'Finish'}
                  </Button>
                )}
                <Anchor fz="sm" fw={600} c={col.purple} mt={10} onClick={openIntegrationSite} style={{ display: 'inline-block' }}>
                  View setup instructions anyway
                </Anchor>
              </>
            ) : (
              <>
                {/* Opening the provider is a new tab — make it obvious the user must return here */}
                {loginUrl && (
                  <Box style={{ width: '100%', textAlign: 'left', marginTop: 14, background: col.amberBg2, border: `1px solid ${col.amberBorder2}`, borderRadius: 10, padding: '11px 13px' }}>
                    <Group spacing={9} noWrap align="flex-start">
                      <div style={{ width: 26, height: 26, borderRadius: 8, background: col.amberBg, color: col.redText, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                        <IconArrowBackUp size={16} />
                      </div>
                      <Text fz={13} c={col.redText}>
                        <b>{current.label} opens in a new tab.</b> After that, come back to this
                        window — we'll walk you through each step and confirm the connection.
                      </Text>
                    </Group>
                  </Box>
                )}
                <Button
                  className={classes.gsPurpleBtn}
                  mt={14}
                  rightIcon={loginUrl ? <IconArrowUpRight size={18} /> : undefined}
                  onClick={openIntegrationSite}
                >
                  {loginUrl ? `Open ${current.label} & Continue` : 'Continue to Setup Instructions'}
                </Button>
              </>
            )}
          </Box>
        )}

        {/* Guided steps */}
        {phase === 'steps' && (
          <Box style={{ marginTop: 16 }}>
            <UrlBanner />
            <Box style={{ ...centered, marginTop: 14 }}>
              <Text fw={800} fz={26} style={{ fontFamily: 'Georgia, serif' }}>
                {current.label} Setup
              </Text>
              <Text c="dimmed" fz="sm" maw={480}>
                {loginUrl
                  ? `Follow these steps to add your webhook inside ${current.label}. We'll walk you through each one.`
                  : 'Follow these steps to send your order data to Visiopt. We\'ll walk you through each one.'}
              </Text>
              <StepVideo modalTitle={`${current.label} setup walkthrough`} sublabel="A 45-second walkthrough of this step" />
              <Text fw={700} fz="sm" c={col.purple} mt={6} style={{ letterSpacing: '0.04em' }}>
                {current.label.toUpperCase()} SETUP · STEP {subStep + 1} OF {steps.length}
              </Text>
              <div className={classes.gsDots}>
                {steps.map((_, i) => (
                  <span key={i} className={cx(classes.gsDot, { [classes.gsDotActive]: i === subStep, [classes.gsDotDone]: i < subStep })} />
                ))}
              </div>
              <Text fz={13} c="dimmed">
                {loginUrl ? (
                  <>
                    Complete this step in {current.label}, then return here and click <b>I've Completed This Step</b>.
                  </>
                ) : (
                  <>
                    Complete this step, then click <b>I've Completed This Step</b>.
                  </>
                )}
              </Text>
              <Text fw={700} fz="md" c={col.textPrimary} mt={4}>
                {activeStep.text.replace('{X}', current.label)}
              </Text>

              {activeStep.points && activeStep.points.length > 0 && (
                <Box component="ul" style={{ margin: '8px 0 0', paddingLeft: 20, textAlign: 'left', width: '100%', display: 'grid', gap: 4 }}>
                  {activeStep.points.map((p, i) => (
                    <li key={i}>
                      <Text fz={14} c={col.textSecondary}>
                        {p.replace('{X}', current.label)}
                      </Text>
                    </li>
                  ))}
                </Box>
              )}

              {activeStep.code && (
                <Box style={{ width: '100%', position: 'relative', marginTop: 10 }}>
                  <Box style={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                    <CopyButton value={activeStep.code} timeout={1500}>
                      {({ copied, copy }) => (
                        <Button size="xs" radius="md" variant={copied ? 'filled' : 'default'} color="dark" leftIcon={<IconCopy size={13} />} onClick={copy}>
                          {copied ? 'Copied' : 'Copy'}
                        </Button>
                      )}
                    </CopyButton>
                  </Box>
                  <Box
                    component="pre"
                    style={{
                      margin: 0,
                      padding: '14px 16px',
                      paddingRight: 90,
                      background: '#0F172A',
                      color: '#E2E8F0',
                      borderRadius: 10,
                      overflowX: 'auto',
                      textAlign: 'left',
                      fontSize: 12.5,
                      lineHeight: 1.6,
                      fontFamily: 'ui-monospace, Menlo, monospace',
                    }}
                  >
                    {activeStep.code}
                  </Box>
                </Box>
              )}

              {activeStep.docUrl && (
                <Group position="left" style={{ width: '100%', marginTop: 10 }}>
                  <Anchor
                    href={activeStep.docUrl}
                    target="_blank"
                    rel="noreferrer"
                    fz={13.5}
                    fw={700}
                    c={col.purple}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    <IconArrowUpRight size={15} /> View official {current.label} documentation
                  </Anchor>
                </Group>
              )}

              {activeStep.copyUrl ? (
                <Group spacing={10} noWrap style={{ width: '100%', marginTop: 8 }}>
                  <TextInput style={{ flex: 1 }} size="md" radius="md" readOnly value={url} />
                  <CopyButton value={url} timeout={1500}>
                    {({ copied, copy }) => (
                      <Button color="dark" radius="md" h={42} onClick={copy}>
                        {copied ? 'Copied' : 'Copy URL'}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              ) : activeStep.image ? (
                <StepImage
                  key={activeStep.image}
                  src={activeStep.image}
                  alt={`${current.label} — step ${subStep + 1}`}
                />
              ) : null}

              <Group mt={16}>
                {subStep > 0 && (
                  <Button variant="default" radius="md" h={44} onClick={() => setSubStep(subStep - 1)}>
                    Back
                  </Button>
                )}
                <Button
                  className={classes.gsPurpleBtn}
                  h={44}
                  onClick={() => (subStep < steps.length - 1 ? setSubStep(subStep + 1) : setPhase('complete'))}
                >
                  I've Completed This Step
                </Button>
              </Group>
            </Box>
          </Box>
        )}

        {/* Integration complete */}
        {phase === 'complete' && (
          <Box style={{ ...centered, marginTop: 18 }}>
            <SuccessCheck size={72} />
            <Text fw={800} fz={26} style={{ fontFamily: 'Georgia, serif' }}>
              {current.label} Setup Complete
            </Text>
            <Text c="dimmed" fz="sm" maw={460}>
              {current.label} has been configured to send events to Visiopt.
            </Text>
            {verificationBlock}
            <StepVideo duration="0:25" sublabel="A 25-second walkthrough of this step" modalTitle={`${current.label} — what's next`} />
            <div className={classes.qaBox} style={{ background: col.purpleBg2, borderColor: col.purpleBorder }}>
              <Text fw={700} fz="sm" c={col.purple} mb={6} style={{ letterSpacing: '0.04em' }}>
                NEXT STEP
              </Text>
              <Text className={classes.qaText}>
                Your {current.label} integration has now been configured successfully.
                {nextItem
                  ? ' If you selected additional integrations, we\'ll guide you through those next.'
                  : " Next, we'll take you to your Visiopt dashboard where we'll walk you through creating your first campaign, launching your first optimization test, and verifying your tracking."}
              </Text>
              <Text className={classes.qaText} mt={8}>
                You don't need to perform any testing yet.
              </Text>
            </div>
            {/* Optional Advanced Tracking — shown for every provider */}
            <div className={classes.qaBox} style={{ background: col.purpleBg2, borderColor: col.purpleBorder, textAlign: 'left' }}>
              <Text fw={700} fz="sm" c={col.textTitle} mb={6}>
                Optional Advanced Tracking
              </Text>
              <Text className={classes.qaText}>
                Your main webhook is now configured and ready to track conversions.
              </Text>
              <Text className={classes.qaText} mt={8}>
                If you need more advanced webhook tracking in the future, Visiopt also supports:
              </Text>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                <li><Text className={classes.qaText} component="span">Track individual products or plans separately</Text></li>
                <li><Text className={classes.qaText} component="span">Track opt-ins, surveys, quizzes, and other events</Text></li>
                <li><Text className={classes.qaText} component="span">Merge multiple webhooks into one reporting goal</Text></li>
                <li><Text className={classes.qaText} component="span">Work with systems that only support a single webhook connection</Text></li>
              </ul>
              <Anchor href={ADVANCED_WEBHOOK_DOC_URL} target="_blank" rel="noreferrer" fw={700} c={col.purple} fz="sm" mt={10} style={{ display: 'inline-block' }}>
                Learn about Advanced Webhook Settings
              </Anchor>
            </div>

            {/* Platform-Specific Guides — only for providers with curated scenarios */}
            {platformGuides && (
              <div className={classes.qaBox} style={{ background: col.purpleBg2, borderColor: col.purpleBorder, textAlign: 'left' }}>
                <Text fw={700} fz="sm" c={col.textTitle} mb={6}>
                  Platform-Specific Guides
                </Text>
                <Text className={classes.qaText}>
                  Need help configuring additional webhooks for {platformGuides.label}?
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  {platformGuides.links.map((lnk) => (
                    <Anchor key={lnk.url} href={lnk.url} target="_blank" rel="noreferrer" fw={700} c={col.purple} fz="sm">
                      {lnk.label}
                    </Anchor>
                  ))}
                </div>
              </div>
            )}
            <Button className={classes.gsPurpleBtn} mt={14} onClick={gotoNextIntegration}>
              {nextItem ? `Continue to ${nextItem.label} Setup` : 'Begin Optimizing'}
            </Button>
          </Box>
        )}
      </Modal>

      {/* Edit funnel name — saves via the existing add_funnel API */}
      <Modal
        opened={funnelModalOpen}
        onClose={() => setFunnelModalOpen(false)}
        centered
        radius="lg"
        size={460}
        title={
          <Group spacing={8}>
            <IconFilter size={18} color={col.purple} />
            <Text fw={700}>Rename Funnel</Text>
          </Group>
        }
      >
        <Text className={classes.fieldLabel}>Funnel Name</Text>
        <TextInput
          size="md"
          radius="md"
          value={funnelNameInput}
          error={funnelError}
          placeholder="Example: Main Sales Funnel"
          onChange={(e) => {
            setFunnelNameInput(e.currentTarget.value);
            if (funnelError) setFunnelError('');
          }}
        />
        <Text className={classes.fieldHelp}>This updates the funnel you created in the previous step.</Text>
        <Group position="right" mt={18}>
          <Button variant="default" radius="md" disabled={savingFunnel} onClick={() => setFunnelModalOpen(false)}>
            Cancel
          </Button>
          <Button className={classes.gsPurpleBtn} loading={savingFunnel} onClick={saveFunnelName}>
            Save Changes
          </Button>
        </Group>
      </Modal>
    </>
  );
}
